import * as React from 'react';
import { ChevronUp, ChevronDown, MoreVertical } from 'lucide-react';

export interface Column<T = any> {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  width?: string;
  render?: (value: any, row: T) => React.ReactNode;
}

export interface TableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  isLoading?: boolean;
  emptyMessage?: string;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
}

/**
 * Standardized Table Component
 * 
 * Design System Specifications:
 * - Header: Light gray background (#f5f5f5)
 * - Borders: Light gray (#e9e9e9)
 * - Hover: Subtle background change
 * - Sortable columns with indicators
 * 
 * Usage:
 * ```tsx
 * <Table
 *   columns={[
 *     { key: 'name', label: 'Name', sortable: true },
 *     { key: 'email', label: 'Email' },
 *     { 
 *       key: 'status', 
 *       label: 'Status',
 *       render: (value) => <Badge>{value}</Badge>
 *     }
 *   ]}
 *   data={users}
 *   onRowClick={(row) => console.log(row)}
 * />
 * ```
 */
export function Table<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  onSort,
  isLoading = false,
  emptyMessage = 'Keine Daten vorhanden',
  striped = false,
  hoverable = true,
  compact = false,
}: TableProps<T>) {
  const [sortKey, setSortKey] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

  const handleSort = (key: string) => {
    const newDirection = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDirection(newDirection);
    onSort?.(key, newDirection);
  };

  const paddingClass = compact ? 'px-4 py-2' : 'px-4 py-3';

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#f5f5f5] border-b border-[#e9e9e9]">
            {columns.map((column) => {
              const isSorted = sortKey === column.key;
              const alignClass = {
                left: 'text-left',
                center: 'text-center',
                right: 'text-right',
              }[column.align || 'left'];

              return (
                <th
                  key={column.key}
                  className={`${paddingClass} ${alignClass} text-[#1c1c1c] text-[14px] font-medium whitespace-nowrap`}
                  style={{ width: column.width }}
                >
                  {column.sortable ? (
                    <button
                      onClick={() => handleSort(column.key)}
                      className="inline-flex items-center gap-1 hover:text-[#0084c7] transition-colors"
                    >
                      {column.label}
                      {isSorted && (
                        sortDirection === 'asc' ? (
                          <ChevronUp className="size-4" />
                        ) : (
                          <ChevronDown className="size-4" />
                        )
                      )}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8">
                <div className="inline-flex items-center gap-2 text-[#6b6b6b]">
                  <div className="size-5 border-2 border-[#0084c7] border-t-transparent rounded-full animate-spin" />
                  Lädt...
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-[#6b6b6b]">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => {
              const isStriped = striped && rowIndex % 2 === 1;
              const bgClass = isStriped ? 'bg-[#f5f5f5]/50' : 'bg-white';
              const hoverClass = hoverable ? 'hover:bg-[#f5f5f5] transition-colors' : '';
              const clickableClass = onRowClick ? 'cursor-pointer' : '';

              return (
                <tr
                  key={rowIndex}
                  className={`border-b border-[#e9e9e9] ${bgClass} ${hoverClass} ${clickableClass}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column) => {
                    const value = row[column.key];
                    const alignClass = {
                      left: 'text-left',
                      center: 'text-center',
                      right: 'text-right',
                    }[column.align || 'left'];

                    return (
                      <td
                        key={`${rowIndex}-${column.key}`}
                        className={`${paddingClass} ${alignClass} text-[#1c1c1c] text-[14px]`}
                      >
                        {column.render ? column.render(value, row) : value}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Table Actions Component
// ============================================

export interface TableAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

export interface TableActionsProps {
  actions: TableAction[];
}

export function TableActions({ actions }: TableActionsProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 hover:bg-[#f5f5f5] rounded-[6px] transition-colors"
      >
        <MoreVertical className="size-4 text-[#6b6b6b]" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-[#e9e9e9] rounded-[10px] shadow-lg min-w-[160px] z-10">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick();
                setIsOpen(false);
              }}
              className={`
                w-full flex items-center gap-2 px-4 py-2 text-[14px] text-left transition-colors
                ${action.variant === 'danger' 
                  ? 'text-[#dc2626] hover:bg-red-50' 
                  : 'text-[#1c1c1c] hover:bg-[#f5f5f5]'}
                ${index === 0 ? 'rounded-t-[10px]' : ''}
                ${index === actions.length - 1 ? 'rounded-b-[10px]' : ''}
              `}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Pagination Component
// ============================================

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showPageSize?: boolean;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  showPageSize = false,
  pageSize = 10,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}: PaginationProps) {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      {showPageSize && onPageSizeChange && (
        <div className="flex items-center gap-2">
          <span className="text-[#6b6b6b] text-[14px]">Einträge pro Seite:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-[36px] px-3 border border-[#e9e9e9] rounded-[8px] text-[14px] text-[#1c1c1c] focus:outline-none focus:border-[#0084c7] focus:ring-2 focus:ring-[#0084c7]/20"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-[36px] px-3 rounded-[8px] border border-[#e9e9e9] text-[#1c1c1c] hover:bg-[#f5f5f5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[14px]"
        >
          Zurück
        </button>

        {getPageNumbers().map((page, index) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${index}`} className="px-2 text-[#6b6b6b]">
                ...
              </span>
            );
          }

          const isActive = page === currentPage;
          return (
            <button
              key={page}
              onClick={() => onPageChange(page as number)}
              className={`
                h-[36px] min-w-[36px] px-2 rounded-[8px] text-[14px] transition-colors
                ${isActive 
                  ? 'bg-[#0084c7] text-white' 
                  : 'border border-[#e9e9e9] text-[#1c1c1c] hover:bg-[#f5f5f5]'}
              `}
            >
              {page}
            </button>
          );
        })}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-[36px] px-3 rounded-[8px] border border-[#e9e9e9] text-[#1c1c1c] hover:bg-[#f5f5f5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[14px]"
        >
          Weiter
        </button>
      </div>
    </div>
  );
}
